/**
 * Die Höhe der Anwendung und der Abstand für die Tastatur.
 *
 * **Zwei Zahlen, ein Beobachter.** Beide hängen an derselben Messung, und
 * getrennt würden sie auseinanderlaufen: `--app-hoehe` ist, wie hoch die
 * Anwendung sein soll, `--tastatur-hoehe`, wie viel davon unten frei bleibt.
 *
 * **Datei und Funktion heißen trotzdem weiter nach der Tastatur.** Der Name
 * ist damit zu eng, und das ist eine bewusste Entscheidung: hier steht die
 * ganze Vorgeschichte des Tastaturabstands, samt eines verworfenen ersten
 * Anlaufs, und ein `git mv` schnitte sie von ihrer Herkunft ab. Der Bruch
 * steht dafür hier erklärt — genauso wie beim Formular am Highlight.
 *
 * # `--app-hoehe`: die Höhe wird gemessen, nicht behauptet
 *
 * Aus dem Betrieb, zwei Bildschirmfotos: auf einem iPhone 17 Pro Max blieben
 * unter der Eingabezeile 60 Punkte in `--page` frei -- der Farbe des
 * *Dokuments*, nicht der der Leiste. Der Streifen lag also außerhalb der
 * Anwendung. Auf einem iPad war es das Gegenteil: die Eingabezeile lag
 * *unter* dem Fensterrand und ließ sich nicht heranholen.
 *
 * Davor stand dort `100dvh`, und genau dafür war es eingebaut worden. Einmal
 * zu kurz, einmal zu lang: **`dvh` ist auf diesen Geräten nicht das, was man
 * sieht.** Welche Zahl der Browser dort einsetzt, ließ sich von außen nicht
 * feststellen -- und noch einmal zu raten wäre der dritte Anlauf mit
 * derselben Methode gewesen.
 *
 * Also `window.innerHeight`, gemessen und nachgeführt. Und ausdrücklich
 * **nicht** `visualViewport.height`: die schrumpft unter iOS mit der
 * Tastatur, und genau daran ist der erste Anlauf gescheitert (siehe unten).
 * `innerHeight` tut das dort nicht. Wo ein Browser das Fenster von sich aus
 * verkleinert (Android), schrumpft `innerHeight` mit -- dort ist der
 * Tastaturabstand ohnehin 0, es wird also nichts doppelt gezählt.
 *
 * # `--tastatur-hoehe`: der Abstand
 *
 * Aus dem Betrieb, auf dem iPhone: Chat öffnen, ins Feld tippen — und das Feld
 * liegt unter der Tastatur. Auf dem iPad passt es.
 *
 * Der Grund ist ein alter Unterschied in iOS-Safari: die Tastatur verkleinert
 * dort **nicht das Fenster**, sondern legt sich darüber. `height: 100%` misst
 * weiter die volle Höhe, die Anwendung reicht also hinter die Tastatur, und
 * was unten sitzt — hier die Eingabezeile — bleibt darunter liegen.
 *
 * Gemessen wird deshalb nicht die Höhe, sondern die **Überdeckung**: was das
 * Fenster hat und die sichtbare Fläche nicht. Genau dieser Betrag steht als
 * `--tastatur-hoehe` bereit, und die Anwendung lässt ihn unten frei.
 *
 * Der erste Anlauf hat stattdessen die Höhe von `html`, `body` und `#root` auf
 * die sichtbare Fläche gesetzt — und damit einen schlimmeren Fehler gebaut:
 * eine Anwendung, die kleiner ist als das Fenster, liegt irgendwo darin, und
 * wo, entscheidet iOS. Schiebt Safari die Fläche, um das Eingabefeld
 * freizustellen, rutscht sie aus dem Blick. Auf dem Gerät war dann gar nichts
 * mehr zu sehen, nur noch die Tastatur. Ein Abstand kann das nicht: die
 * Anwendung bleibt so groß wie das Fenster und an ihrem Platz. Stimmt der
 * Betrag einmal nicht, steht man da, wo man vorher stand — mehr nicht.
 *
 * Nicht genommen wurden zwei naheliegende Wege: `100dvh` kennt die Tastatur
 * nicht, nur die Browserleisten, und `interactive-widget=resizes-content` im
 * Viewport-Meta wäre ein zweiter Mechanismus neben diesem, dessen Rückhalt in
 * Safari offen ist — zwei, die sich in die Quere kommen können, sind schlechter
 * als einer.
 */

/**
 * Darunter ist es eine Browserleiste und keine Tastatur.
 *
 * **Die Schwelle allein reicht nicht**, und das kam aus dem Betrieb: unter der
 * Eingabezeile blieb ein Streifen leer. Eine überlagernde Browserleiste macht
 * dieselbe Differenz wie eine kleine Tastatur; die Zahl trennt die Fälle
 * nicht. Sie bleibt trotzdem stehen, gegen Ausreißer in der Messung.
 */
const SCHWELLE = 80

/**
 * Steht der Cursor wirklich in einem Schreibfeld?
 *
 * **Ohne Fokus keine Tastatur.** Das ist kein Schwellenwert, sondern die
 * Sache selbst -- und der einzige Weg, eine Tastatur von einer Browserleiste
 * zu unterscheiden, die sich über die Seite legt.
 */
function schreibtJemand(): boolean {
  const el = document.activeElement
  if (!(el instanceof HTMLElement)) return false
  return (
    el instanceof HTMLInputElement
    || el instanceof HTMLTextAreaElement
    || el.isContentEditable
  )
}

/**
 * Darunter wird nichts gesetzt.
 *
 * Ein Ausreißer während einer Drehung darf die Anwendung nicht zusammenfalten
 * -- dieselbe Haltung wie der Deckel beim Tastaturabstand. Kein Fenster, in
 * dem diese Anwendung sinnvoll läuft, ist 200 Punkte hoch.
 *
 * **Hieß einmal `BODEN`, und das war ein zweites Wort für eine andere Sache:**
 * die Zahl aus `vollbildHoehe()` heißt jetzt Mindesthöhe, und zwei Bedeutungen
 * für ein Wort sind genau das, was die Sprachregeln hier vermeiden sollen.
 * Diese Zahl ist keine Mindesthöhe, sondern eine Verwerfungsgrenze.
 */
const UNSINNIG_KLEIN = 200

/**
 * So viel Anwendung bleibt mindestens stehen, wenn unten Platz frei gehalten
 * wird.
 *
 * **Der Deckel war die halbe Anwendung, und das war zu eng.** Aus dem Betrieb,
 * iPhone 16 Pro, mit Mitschrieb und Bildschirmfoto: von 874 Punkten blieben
 * bei offener Tastatur noch 416 sichtbar -- die Tastatur verdeckt dort
 * **458 Punkte, also mehr als die Hälfte**. Der Deckel schnitt den Abstand auf
 * 437 ab; damit deckte der freigehaltene Platz den Überstand nicht mehr, die
 * Rückholung unten griff nicht, und die Seite blieb, wo iOS sie hingeschoben
 * hatte: die Eingabezeile stand am oberen Rand, darunter der leere
 * freigehaltene Streifen. Genau das zeigt das Bildschirmfoto.
 *
 * Ein Deckel soll Ausreißer abfangen, keine echte Tastatur. Deshalb steht er
 * jetzt an der anderen Seite: es bleibt ein Streifen Anwendung stehen, und was
 * darüber hinausgeht, ist die Messung, der niemand glauben muss. Auf dem
 * iPhone 17 Pro Max ändert das nichts (956 − 543 = 413, der Deckel lag dort
 * nie an).
 */
const MINDESTINHALT = 180

/**
 * Wie viel Platz unten freigehalten wird -- die Regel für sich allein.
 *
 * Hier und nicht mitten in `abstandNachziehen()`, weil genau diese Rechnung
 * den Fehler vom iPhone 16 Pro gemacht hat und ein Prüflauf sie sonst nicht
 * messen kann: alles andere in jener Funktion greift auf das Dokument zu.
 */
export function abstandAus(kastenHoehe: number, roh: number, tippt: boolean): number {
  if (roh < SCHWELLE || !tippt) return 0
  const deckel = Math.max(SCHWELLE, kastenHoehe - MINDESTINHALT)
  return Math.min(Math.round(roh), Math.round(deckel))
}

/**
 * Darf die Seite zurück nach oben?
 *
 * Zwei Fälle, und der zweite ist neu: **ohne Fokus immer.** Ohne Feld gibt es
 * nichts freizustellen, und die Regel dieser Datei lautet „die Seite selbst
 * steht immer oben". Im Mitschrieb vom iPhone 16 Pro stand genau dieser
 * Zustand fest: `Abstand 0`, aber `Seite 431` verschoben -- die Bedingung
 * „der freigehaltene Platz deckt den Überstand" kann mit 0 nie erfüllt sein,
 * und `hoeheNachmessen()` (das zurückholt) hängt an anderen Weckrufen als das
 * Scrollen. Die Seite blieb also verschoben stehen, und zu sehen war der
 * leere Streifen unter der Eingabezeile.
 */
export function darfZurueck(roh: number, abstand: number, tippt: boolean): boolean {
  if (!tippt) return true
  return Math.round(roh) <= Math.max(abstand, SCHWELLE - 1)
}

/**
 * Ob die Bildschirmtastatur die Anwendung verdeckt -- **dieselbe Zahl, die
 * den Abstand setzt**, und sonst nichts (0.47.1).
 *
 * Hier stand ein zweiter Test mit eigener Rechnung (Fläche gegen den
 * Bildschirm, sonst Fenster gegen Fläche). Am iPhone 16 Pro meldete iOS
 * beim Öffnen für eine Sekunde ein Fenster von 416 bei einer Fläche von
 * 416: der Abstand stimmte (396), der zweite Test ergab 0 -- also „nicht
 * verdeckt" --, und die 34 Punkte Sicherheitsabstand blieben stehen, bis die
 * Wache mit Fenster 812 nachrechnete. Die Eingabezeile sprang. Zwei Tests
 * für dieselbe Frage laufen auseinander; jetzt gibt es einen.
 *
 * `tippt` bleibt daneben als zweiter Halt (CLAUDE.md): ohne Fokus gibt es
 * keine Bildschirmtastatur, gleichgültig, was der Abstand sagt.
 */
export function verdecktAus(abstand: number, tippt: boolean): boolean {
  return abstand > 0 && tippt
}

/**
 * Welche Höhe die Anwendung beim Schreiben behält, wenn keine Mindesthöhe
 * vom Bildschirm gilt (0.47.1): die zuletzt **ohne** Tastatur gemessene, bei
 * gleicher Fensterbreite. Ein Fenster, das beim Tippen schrumpft, ist die
 * Tastatur und keine Drehung -- eine Drehung ändert die Breite, und dann
 * gilt die Erinnerung nicht.
 */
export function ruheHoeheAus(
  fenster: number,
  mindest: number,
  ruhe: { breite: number; hoehe: number } | null,
  breite: number,
  tippt: boolean,
): number {
  const gehalten = tippt && ruhe !== null && ruhe.breite === breite ? ruhe.hoehe : 0
  return Math.max(fenster, mindest, gehalten)
}

/**
 * Läuft das hier als Web-App vom Home-Bildschirm?
 *
 * **`navigator.standalone` allein reicht nicht**, und das kam aus dem Betrieb:
 * mit der Mindesthöhe aus `d4b4176` stand auf dem iPhone weiterhin
 * `Gesetzte Höhe: 812px` bei `Bildschirm: 402 × 874`, auf dem iPad `1000` bei
 * `1032` -- sie griff auf keinem der beiden Geräte.
 *
 * Der Grund ist Apples Vorläufer selbst: `navigator.standalone` ist nie
 * standardisiert worden und **auf iPadOS gar nicht vorhanden**. Eine Prüfung
 * darauf schließt also genau das Gerät aus, auf dem der Fehler zuerst gemeldet
 * wurde.
 *
 * Der Standard dafür ist die Medienabfrage `display-mode`. Sie steht zuerst;
 * `navigator.standalone` bleibt als Rückfall für ältere Safari-Fassungen
 * daneben. Abgefragt werden alle drei Betriebsarten, die kein Browser-Tab
 * sind -- die Anzeigeart steht im `manifest.webmanifest` und kann sich ändern,
 * ohne dass jemand hier nachsieht.
 */
function alsApp(): boolean {
  const frage = window.matchMedia?.bind(window)
  if (frage !== undefined) {
    for (const art of ['standalone', 'fullscreen', 'minimal-ui']) {
      if (frage(`(display-mode: ${art})`).matches) return true
    }
  }
  // Apples Vorläufer: auf dem iPhone seit jeher, auf dem iPad nie.
  return (navigator as unknown as { standalone?: boolean }).standalone === true
}

/**
 * Die sichtbare Fläche — geprüft, nicht geglaubt.
 *
 * **Aus dem Betrieb, mit Mitschrieb:** im Chat stand bei offener Tastatur ein
 * breiter Zwischenraum zwischen Eingabezeile und Tastatur, und in *jeder*
 * Zeile des Mitschriebs stand `sichtbar = −33554432`. iOS meldete für
 * `visualViewport.height` einen Müllwert (−2²⁵) — dauerhaft, über
 * Seitenwechsel hinweg, mutmaßlich seit die Tastatur einmal ohne Fokusverlust
 * verschwand (#31). Kein Leser prüfte den Wert, und ein Wert machte drei
 * Symptome: der Abstand lief in seinen Deckel (die halbe Anwendung frei),
 * jede Tastatur galt als Bildschirmtastatur, und mit so viel Reserve ließ
 * sich die Überschrift aus dem Bild schieben.
 *
 * `--app-hoehe` hat seine Verwerfungsgrenze längst (`UNSINNIG_KLEIN`); die
 * sichtbare Fläche hatte keine. Das ist dieselbe Haltung: ein unmöglicher
 * Messwert wird **verworfen, nicht verrechnet.** Unmöglich heißt hier: nicht
 * endlich, nicht positiv, höher als die lange Bildschirmseite (plus
 * Schwelle), oder weiter verschoben, als der Bildschirm überhaupt reicht.
 *
 * **Verworfen heißt: das Fenster übernimmt.** Der erste Rückfall war
 * „Abstand 0, iOS stellt schon frei" -- und der Mitschrieb vom Gerät hat ihn
 * widerlegt: iOS' Freistellen ist im verkeilten Zustand selbst kaputt, die
 * Seite sprang zwischen verschoben und nicht verschoben, und dazwischen lag
 * das Eingabefeld unter der Tastatur, bis das Tippen es hervorholte.
 * Derselbe Mitschrieb zeigt aber auch, was verlässlich geblieben ist:
 * `innerHeight` ging sauber 874 → 472 → 874 mit der Tastatur mit, während
 * die sichtbare Fläche Müll meldete. In der Web-App schrumpft das Fenster
 * mit der Bildschirmtastatur -- also trägt es beide Antworten auch allein:
 * der Überstand der Anwendung unter der Fensterkante ist das Verdeckte, und
 * ein Fenster deutlich unter der Bildschirmhöhe heißt Bildschirmtastatur.
 */
function sichtMasse(): { hoehe: number, versatz: number } | null {
  const sicht = window.visualViewport
  if (!sicht) return null
  const hoehe = sicht.height
  const versatz = sicht.offsetTop
  if (!Number.isFinite(hoehe) || !Number.isFinite(versatz)) return null
  const lang = Math.max(window.screen.width, window.screen.height)
  if (hoehe <= 0 || hoehe > lang + SCHWELLE) return null
  if (Math.abs(versatz) > lang) return null
  return { hoehe, versatz }
}

/**
 * Die letzte **verlässlich gemessene** Höhe der Bildschirmtastatur — und der
 * Glaube, ob sie gerade offen ist.
 *
 * **Für die dritte Spielart des verkeilten Zustands.** Aus dem Betrieb, mit
 * Bildschirmfoto: „die Eingabezeile im Chat ist nach wie vor erst mit Eingabe
 * da." In dieser Spielart lügen **beide** Quellen zugleich -- die sichtbare
 * Fläche meldet Müll (−33554432) und das Fenster bleibt bei 874 stehen,
 * obwohl die Tastatur sichtbar offen ist. Es gibt dann nichts Verlässliches mehr
 * zu messen, und der Rückfall über das Fenster greift ins Leere: das Feld
 * lag unter der Tastatur, bis das Tippen den Cursor hervorholte.
 *
 * Was bleibt, ist eine **Messung von vorhin**: sobald eine Quelle einmal
 * verlässlich war (die Fläche plausibel und klein, oder das Fenster geschrumpft),
 * wird die Tastaturhöhe gemerkt. Und ein **Glaube**, der nur aus verlässlichen
 * Signalen und dem Fokus gespeist wird: `focusin` heißt vermutlich offen,
 * `focusout` heißt zu, ein gemessenes Schließen (Fenster oder
 * Fläche wachsen zurück) heißt zu. Die dritte Spielart selbst ändert den
 * Glauben nie -- sie ist ja gerade der Zustand, in dem nichts zu sehen ist.
 *
 * **Die bekannte Grenze bleibt benannt:** verschwindet die Tastatur in der
 * dritten Spielart ohne Fokusverlust (Bildschirmfoto beim Tippen), steht der
 * Streifen, bis das Feld losgelassen wird oder ein verlässliches Signal kommt --
 * das ist der Rest von #31, und er ist unmessbar. Und auf einem iPad, das im
 * verkeilten Zustand mit HW-Tastatur schreibt, nachdem in derselben Sitzung
 * eine Bildschirmtastatur gemessen wurde, hält der Glaube fälschlich Platz
 * frei. Beides ist der Preis dafür, dass die Zeile auf dem iPhone überhaupt
 * über der Tastatur steht.
 */
const TASTATUR_MARKE = 'tb.tastatur.hoehe'

let gemerkteTastatur: number | null = (() => {
  // **Die Erinnerung überlebt den Neustart.** Aus dem Betrieb: die Sitzung
  // begann schon verkeilt (`Start · Fläche verworfen`), und die erste
  // Tastatur dieser Sitzung hatte keine Messung, aus der sie hätte schöpfen
  // können -- das Feld blieb unsichtbar, bis das Fenster messbar schrumpfte.
  // Eine Messung der letzten Sitzung auf demselben Gerät ist eine Messung.
  // try/catch wie beim Push-Merkzettel: ein privates Fenster hat keinen
  // Speicher, und das ist kein Fehler.
  try {
    const roh = Number(localStorage.getItem(TASTATUR_MARKE))
    return Number.isFinite(roh) && roh > SCHWELLE && roh < 1000 ? Math.round(roh) : null
  } catch {
    return null
  }
})()

function tastaturMerken(hoehe: number): void {
  gemerkteTastatur = hoehe
  try {
    localStorage.setItem(TASTATUR_MARKE, `${hoehe}`)
  } catch {
    // Ohne Speicher gilt die Erinnerung eben nur für diese Sitzung.
  }
}

let tastaturVermutet = false

/** Der zuletzt gesetzte Abstand -- die eine Zahl hinter `bildschirmtastaturVerdeckt()`. */
let letzterAbstand = 0

/** Die zuletzt ohne Tastatur gemessene Höhe, samt Fensterbreite (0.47.1). */
let ruheHoehe: { breite: number; hoehe: number } | null = null

/**
 * Verwirft die Grenze gerade — obwohl es `visualViewport` gibt?
 *
 * Für die Diagnosekarte: der Rohwert bleibt dort sichtbar (genau er hat
 * diesen Fehler gefunden), und daneben steht, dass er nicht verrechnet wird.
 */
export function sichtVerworfen(): boolean {
  return window.visualViewport != null && sichtMasse() === null
}

/**
 * Ist die Seite gerade mit zwei Fingern vergrößert (1.14.2)?
 *
 * `visualViewport.scale` ist 1 ohne Zoom und wächst mit ihm. Die Grenze
 * liegt knapp über 1, weil iOS beim Zurückzoomen 1,000x meldet; nach oben
 * ist sie ein Schutz gegen einen Müllwert wie bei der Höhe (`sichtMasse()`):
 * stünde dort eine Zahl in den Millionen, bliebe die Messung sonst für immer
 * stehen -- und damit die Tastatur ohne Abstand.
 */
export function zoomAus(scale: number | null | undefined): boolean {
  return typeof scale === 'number' && Number.isFinite(scale) && scale > 1.01 && scale < 20
}

function gezoomt(): boolean {
  return zoomAus(window.visualViewport?.scale)
}

/**
 * Das Merkmal `data-tastatur` — zum Lesen und zum Zuhören.
 *
 * **Warum es das zweimal gibt.** Die Regel dazu steht in `app.css` und blendet
 * den Anhang-Knopf aus; das braucht kein JavaScript. Der Knopf der
 * Sprachnachricht ist ein anderer Fall: an seiner Stelle muss der Sendeknopf
 * stehen, sonst ist die rechte Seite der Zeile leer. Eine Kachel wegnehmen
 * kann CSS, eine andere hinstellen nicht — dafür muss die Oberfläche es
 * wissen.
 *
 * Beobachtet wird das Merkmal und nicht die Messung: so gibt es **eine**
 * Stelle, an der entschieden wird (`abstandNachziehen()`), und alles andere
 * liest ab. Zwei Wege zur selben Frage liefen sonst auseinander.
 *
 * Framework-frei, wie der Rest dieser Datei; der Haken für React steht in
 * `hooks/useTastatur.ts`.
 */
export function tastaturMerkmal(): boolean {
  return document.documentElement.getAttribute('data-tastatur') === 'an'
}

export function tastaturMerkmalAbonnieren(melde: () => void): () => void {
  if (typeof MutationObserver === 'undefined') return () => {}
  const beobachter = new MutationObserver(melde)
  beobachter.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-tastatur'],
  })
  return () => beobachter.disconnect()
}

/**
 * Liegt gerade eine **Bildschirmtastatur** über der Anwendung?
 *
 * **Die Frage ist nicht, ob ein Feld den Fokus hat, sondern womit geschrieben
 * wird.** Aus dem Betrieb (#28): „HW-Tastatur = Senden, Bildschirmtastatur =
 * Leerzeile" -- wie in iMessage und WhatsApp. Dafür braucht es ein Merkmal,
 * das beide Fälle trennt, und ein Merkmal des *Bildschirms* taugt dafür nicht:
 * ein iPad mit angeschlossener Tastatur meldet einen groben Zeiger, und daran
 * ist ein früherer Anlauf gescheitert.
 *
 * **Die Antwort ist der freigehaltene Abstand -- und dass sie das einmal
 * nicht sein konnte, gehört dazugesagt.** Hier stand: „Der erste Anlauf las
 * den freigehaltenen Abstand, und das war falsch", mit dem Mitschrieb
 * `478 / 478 / 812 / 478 · 334↕334`, Abstand 0 bei offener Tastatur. Damals
 * schob iOS die Seite hoch, und die Anwendung rechnete das Verschieben vom
 * Abstand ab -- hatte iOS genug geschoben, blieb 0. Seit #34 ist es
 * umgekehrt: die Verschiebung wird zurückgenommen, und die Anwendung hält
 * den Platz selbst frei. Der Abstand *ist* damit die Tastatur, solange sie
 * offen steht.
 *
 * An seine Stelle war ein zweiter Test mit eigener Rechnung getreten
 * (sichtbare Fläche gegen den Bildschirm, sonst Fenster gegen Fläche, Grenze
 * 80), und der lief mit dem Abstand auseinander (0.47.1): beim Öffnen meldet
 * iOS am iPhone 16 Pro eine Sekunde lang ein Fenster von 416 bei einer
 * Fläche von 416 -- Abstand 396, zweiter Test 0. Die 34 Punkte
 * Sicherheitsabstand blieben stehen, bis die Wache nachrechnete, und die
 * Eingabezeile sprang. Jetzt liest die Sperre dieselbe Zahl, die
 * `abstandNachziehen()` gesetzt hat; die Regel steht in `verdecktAus()`.
 *
 * **Was das auf Android bedeutet, gehört gesagt:** dort verkleinert der
 * Browser das Fenster selbst, der Abstand bleibt 0, und die Eingabetaste
 * sendet wie am Rechner. Das ist nicht die Regel aus der Meldung, aber es ist
 * das bisherige Verhalten -- und ein zweites Merkmal zu erfinden, das keiner
 * nachmessen kann, wäre schlechter als eine benannte Grenze.
 */
export function bildschirmtastaturVerdeckt(): boolean {
  // Was den Abstand setzt, setzt auch die Sperre -- `letzterAbstand` ist die
  // Zahl aus `abstandNachziehen()`, und `verdecktAus()` trägt den Grund.
  return verdecktAus(letzterAbstand, schreibtJemand())
}

/**
 * Was die Mindesthöhe gerade entscheidet -- für die Diagnosekarte.
 *
 * **Damit die nächste Runde nicht wieder aus einem Bildschirmfoto gerechnet
 * wird.** Zwei Anläufe sind daran vorbeigegangen: sie griff auf dem
 * Gerät nicht, und aus den angezeigten Zahlen war nicht zu sehen, an welcher
 * der beiden Bedingungen es lag. Jetzt steht beides da.
 */
export function bodenLage(): { alsApp: boolean, breite: number, boden: number | null } {
  return { alsApp: alsApp(), breite: Math.round(window.innerWidth), boden: vollbildHoehe() }
}

/**
 * Wie hoch das Fenster im Vollbild sein *muss* -- oder `null`.
 *
 * **Weil iOS die Fensterhöhe verliert und sie nicht wiederfindet.** Aus dem
 * Betrieb, zwei Mitschriebe desselben Standes, jeweils Tastatur auf und wieder
 * zu:
 *
 * ```
 * iPhone 16 Pro   874 → 478 (Tastatur) → 812   und bleibt bei 812
 * iPad quer      1032 → 534 (Tastatur) → 1000  und bleibt bei 1000
 * ```
 *
 * 874 − 62 = 812, 1032 − 32 = 1000, und 62 beziehungsweise 32 ist der obere
 * Sicherheitsabstand des Geräts. **Es fehlt genau der Streifen unter der
 * Statusleiste**, und zwar auf Dauer: über Seitenwechsel hinweg, bis die
 * Anwendung neu startet. Sichtbar wird er unten, weil die Anwendung oben
 * angeschlagen bleibt.
 *
 * `100dvh` hilft dagegen nicht: es meldet dieselbe falsche Zahl (812 und 1000
 * in denselben Mitschrieben). `max(100dvh, --app-hoehe)` nimmt damit das
 * Größere von zwei falschen Werten. **`window.screen` ist der einzige, der
 * richtig bleibt** -- also stammt die Mindesthöhe von dort.
 *
 * Zwei Bedingungen, und beide sind nötig:
 *
 * 1. **Nur in der Web-App vom Home-Bildschirm.** In einem Browser-Tab ist ein
 *    kleineres Fenster richtig -- dort stehen Leisten davor, und ein
 *    erzwungenes Vollbild schöbe die Eingabezeile darunter.
 * 2. **Nur, wenn die Breite beweist, dass wir das ganze Bild haben.** `screen`
 *    meldet auf iPadOS immer die Hochkantmaße, egal wie das Gerät liegt. Passt
 *    `innerWidth` auf eine der beiden Seiten, ist es Vollbild und die andere
 *    Seite ist die Höhe. Im geteilten Fenster passt keine -- dann gilt, was der
 *    Browser sagt.
 *
 * Dass die Mindesthöhe auch bei offener Tastatur die richtige Zahl wäre, ist kein
 * Zufall: `--app-hoehe` trägt die *ganze* Fensterhöhe, den Platz für die
 * Tastatur nimmt `--tastatur-hoehe` weg. Zum Zuge kommt er dort trotzdem
 * nicht -- `hoeheNachmessen()` steigt vorher aus, solange jemand schreibt.
 *
 * **Wie erkannt wird, dass es die Web-App ist**, steht in `alsApp()` -- und
 * daran ist der erste Anlauf gescheitert.
 *
 * **Das Wort ist „Mindesthöhe" und nicht „Boden".** Aus dem Betrieb: „was heißt
 * ‚Boden aus dem Bildschirm'? bitte sinnvolle deutsche Übersetzung." Berechtigt
 * -- „Boden" war ein Bild aus dem Englischen (*floor*) und benennt die Sache
 * nicht. Gemeint ist die Höhe, die der Bildschirm mindestens vorgibt; so heißt
 * es jetzt auch in der Diagnosekarte.
 */
export function vollbildHoehe(): number | null {
  return mindesthoeheAus(
    alsApp(),
    sicherheitsabstaende().oben,
    Math.round(window.innerWidth),
    { kurz: Math.min(window.screen.width, window.screen.height),
      lang: Math.max(window.screen.width, window.screen.height) },
  )
}

/**
 * Die vier Sicherheitsabstände, wie sie das Gerät gerade meldet.
 *
 * `env()` lässt sich nur über eine Eigenschaft lesen, die eine Länge nimmt --
 * ein unsichtbarer Kasten mit `padding` tut das. **Hier und nicht ein zweites
 * Mal in der Diagnosekarte:** dort stand es zuerst, und seit die Mindesthöhe
 * am oberen Abstand hängt, entscheidet dieselbe Messung etwas. Zwei Kopien
 * derselben Messung wären genau die Sorte doppelter Regel, die dieses Projekt
 * schon mehrfach auseinanderlaufen sah.
 *
 * **Gemerkt, solange das Fenster gleich bleibt.** Die Abstände ändern sich mit
 * der Lage des Geräts, und die ändert das Fenster; für alles andere wäre ein
 * Kasten je Messung Aufwand ohne Auskunft.
 */
let gemerkteRaender: { schluessel: string, werte: Raender } | null = null

export interface Raender { oben: number, rechts: number, unten: number, links: number }

export function sicherheitsabstaende(): Raender {
  const schluessel = `${Math.round(window.innerWidth)}x${Math.round(window.innerHeight)}`
  if (gemerkteRaender?.schluessel === schluessel) return gemerkteRaender.werte
  const kasten = document.createElement('div')
  kasten.style.cssText
    = 'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;'
    + 'padding:env(safe-area-inset-top) env(safe-area-inset-right)'
    + ' env(safe-area-inset-bottom) env(safe-area-inset-left)'
  document.body.appendChild(kasten)
  const stil = getComputedStyle(kasten)
  const zahl = (wert: string) => {
    const roh = Math.round(parseFloat(wert))
    return Number.isFinite(roh) ? roh : 0
  }
  const werte: Raender = {
    oben: zahl(stil.paddingTop),
    rechts: zahl(stil.paddingRight),
    unten: zahl(stil.paddingBottom),
    links: zahl(stil.paddingLeft),
  }
  kasten.remove()
  gemerkteRaender = { schluessel, werte }
  return werte
}

/**
 * Die Mindesthöhe als Regel für sich allein -- damit ein Lauf sie messen kann.
 *
 * **Der obere Sicherheitsabstand entscheidet, und das ist neu.** Aus dem
 * Betrieb, iPhone 16 Pro, mit Diagnosekarte:
 *
 * ```
 * Höhe der Anwendung: 874 von 812     Fensterhöhe 812   100dvh ergibt 812
 * Bildschirm: 402 × 874               Sicherheitsabstände: 0 / 0 / 34 / 0
 * ```
 *
 * Die Anwendung stand auf 874 in einem Fenster von 812 -- die Eingabezeile
 * hing 62 Punkte unter der Kante und war halb abgeschnitten. **Hier ist das
 * Fenster nicht zu klein, sondern richtig:** der obere Abstand ist `0`, das
 * Fenster beginnt also schon unter der Statusleiste, und die fehlenden 62
 * Punkte sind genau sie. Bildschirmhöhe kann dann nie die Höhe der Anwendung
 * sein.
 *
 * Der Fall, für den die Mindesthöhe gebaut wurde, ist der andere: dort zeichnet
 * die Anwendung **unter** der Statusleiste (`viewport-fit=cover`, oberer
 * Abstand deutlich über 0), und iOS meldet trotzdem die um diesen Streifen
 * gekürzte Höhe -- sichtbar wurde er unten als Band in der Farbe des
 * Dokuments. Genau dann, und nur dann, gilt weiter der Bildschirm.
 *
 * Die zweite Bedingung bleibt, wie sie war: **die Breite muss beweisen, dass
 * wir das ganze Bild haben.** `screen` meldet auf iPadOS immer die
 * Hochkantmaße; im geteilten Fenster passt keine der beiden Seiten, und dann
 * gilt, was der Browser sagt.
 */
export function mindesthoeheAus(
  imVollbild: boolean,
  obenAbstand: number,
  breite: number,
  schirm: { kurz: number, lang: number },
): number | null {
  if (!imVollbild) return null
  if (obenAbstand <= 0) return null
  if (breite === schirm.lang) return schirm.kurz
  if (breite === schirm.kurz) return schirm.lang
  return null
}

/**
 * Die Höhe der Anwendung neu messen.
 *
 * **Nicht, während jemand schreibt.** Das ist der Kern, und er hat einen
 * Fehler gekostet: aus dem Betrieb, mit einer Reihenfolge, die ihn festnagelt
 * -- „Neustart der WebApp: alles ok; kein Rand unten im Chat. Aufruf anderer
 * Menüs (Einstellungen, Tastaturtest …) — danach ist im Chat und in der Karte
 * unten ein Rand."
 *
 * In einer Web-App vom Home-Bildschirm verkleinert iOS beim Öffnen der
 * Tastatur das **Fenster**. `innerHeight` ist dann nicht mehr die Höhe der
 * Anwendung, sondern die des Restes daneben -- und wer diesen Wert festhält,
 * hält den Fehler fest. Die Karte *Einstellungen → Tastatur* fordert zum
 * Tippen auf und ist damit der zuverlässigste Weg, ihn auszulösen; genau
 * deshalb stand sie in der Meldung.
 *
 * Der Tastaturabstand eine Ebene tiefer stellt dieselbe Frage seit jeher
 * (`schreibtJemand()`). Sie hier nicht zu stellen war die Lücke.
 *
 * **Ohne Fokus keine Tastatur** -- und `focusout` löst die Messung ohnehin
 * gleich wieder aus.
 */
export function hoeheNachmessen(anlass = 'messen'): void {
  merken(anlass)
  // **Nicht, während jemand mit zwei Fingern zoomt** (1.14.2). Unter iOS
  // schrumpfen `innerHeight` und `innerWidth` beim Fingerzoom auf die
  // sichtbare Fläche; wer das misst, misst den Ausschnitt und macht die
  // Anwendung so klein wie ihn. Aus dem Betrieb, als Aufnahme vom iPhone:
  // hineingezoomt stand die Seite in der linken oberen Ecke, rechts und
  // unten Grau. Beim Zurückzoomen kommt ein `resize`, und dann wird wieder
  // gemessen -- bis dahin gilt, was vor dem Zoom stand.
  if (gezoomt()) return
  const tippt = schreibtJemand()
  if (!tippt) seiteZurueck()
  // **Es gilt immer die Mindesthöhe — und genau das hält den Kasten still.**
  //
  // Hier stand einmal „beim Schreiben gilt das Fenster", mit dem Mitschrieb
  // aus `a45a0cb` als Begründung: die Mindesthöhe hielt die Anwendung 144
  // Punkte höher als das Fenster, **erst dadurch gab es etwas zu scrollen**,
  // iOS schob die Seite hoch, und dazwischen stand ein breiter Streifen.
  //
  // **Dieser Grund ist von #34 abgelöst worden.** Seitdem holt
  // `abstandNachziehen()` die Verschiebung zurück, sobald der freigehaltene
  // Platz den Überstand deckt -- und er deckt ihn hier genau: der Überstand
  // *ist* der Abstand. Was die alte Regel verhinderte, verhindert jetzt der
  // Rückholer, und zwar an der Stelle, an der es hingehört.
  //
  // Geblieben ist von ihr nur der Schaden, und der stand im Mitschrieb vom
  // iPhone 17 Pro Max:
  //
  //     956 / 543 / 956 / 605     Kasten 956
  //     894 / 543 / 894 / 481     Kasten 894
  //
  // Zwanzig solche Wechsel in einer Sekunde. Das Fenster meldet dort
  // abwechselnd 956, 894 und 543; die alte Regel schrieb bei 543 eben 543 in
  // `--app-hoehe` -- **und damit war die Anwendung dem flackernden `100dvh`
  // ausgeliefert**, denn `html` ist `max(100dvh, --app-hoehe)` und 543 gewinnt
  // dieses `max()` nie. Der Kasten wanderte mit, der Abstand hinterher, und
  // die Eingabezeile sprang zwischen 543 und 605 (Video: sie lag hinter der
  // schwebenden iOS-Leiste).
  //
  // **`100dvh` kann den Bildschirm nicht überschreiten.** Steht in
  // `--app-hoehe` die Mindesthöhe, gewinnt sie das `max()` also *immer*, und
  // der Kasten steht still -- gleichgültig, warum `dvh` flackert. Das ist der
  // Unterschied zwischen dem Flackern nachrechnen und es abstellen.
  //
  // **Kleiner als vorher wird die Zahl damit nie**, und der Grund aus
  // `b164f11` (eine zu kleine Zahl beim Schreiben festgehalten) ist erst recht
  // gewahrt: festgehalten wird jetzt gar keine kleine Zahl mehr.
  //
  // Wo es keine Mindesthöhe gibt -- Browser-Tab, Android, geteiltes Fenster --
  // ist sie 0, und es bleibt beim Fenster wie bisher.
  const mindest = vollbildHoehe() ?? 0
  const fenster = Math.round(window.innerHeight)
  const breite = Math.round(window.innerWidth)
  // **Und wo keine Mindesthöhe gilt, hält die Ruhehöhe** (0.47.1). Am iPhone
  // 16 Pro mit oberem Sicherheitsabstand 0 gibt es keine; beim Öffnen der
  // Tastatur stand hier für eine Sekunde 416 statt 812. Sichtbar wurde das
  // nur nicht, weil `100dvh` dort still blieb -- wo es mitschrumpft, wäre
  // es das Flackern vom iPhone 17 Pro Max ohne den Boden.
  const hoch = ruheHoeheAus(fenster, mindest, ruheHoehe, breite, tippt)
  // Ein Ausreißer während einer Drehung darf die Anwendung nicht
  // zusammenfalten.
  if (hoch < UNSINNIG_KLEIN) return
  if (!tippt) ruheHoehe = { breite, hoehe: hoch }
  document.documentElement.style.setProperty('--app-hoehe', `${hoch}px`)
  merken(`${anlass} → ${hoch}`)
}

/**
 * Die Seite selbst steht immer oben.
 *
 * **Aus dem Betrieb, an der Personenverwaltung:** „sobald die Tastatur
 * eingeblendet wird, sieht's komisch aus [...] und so, wenn die Tastatur wieder
 * weg ist — man kann auch nicht mehr ganz nach oben scrollen." Am
 * Bildschirmfoto ausgemessen: die Anwendung endete bei 655 von 874 Punkten,
 * darunter standen 219 Punkte in der Farbe des Dokuments -- und **die
 * Kopfzeile fehlte oben**. Die Anwendung war also nicht zu klein, sondern
 * nach oben geschoben.
 *
 * Der Grund ist iOS: um ein Eingabefeld über der Tastatur freizustellen,
 * scrollt es die **Seite**. Möglich ist das, weil die Anwendung so hoch ist wie
 * das ganze Fenster, das Fenster mit der Tastatur aber schrumpft -- damit gibt
 * es plötzlich etwas zu scrollen. Nach dem Schließen bleibt die Seite stehen,
 * wo sie steht.
 *
 * `body { overflow: hidden }` in `app.css` sagt genau das schon, und iOS hält
 * sich beim Freistellen nicht daran. Also wird es nachgezogen.
 *
 * **Wer zurückholen darf, entscheidet der Aufrufer, und zwar an einer Sache:
 * steht das Feld danach noch über der Tastatur?** Hier stand einmal „nur, wenn
 * niemand schreibt" -- das war zu grob in beide Richtungen. Zu eng, denn bei
 * offener Tastatur blieb die Seite verschoben und die Kopfzeile draußen (#34);
 * zu weit, weil `f60ad9a` die Regel gelockert hat und im verkeilten Zustand die
 * Eingabezeile unter der Tastatur verschwand. `abstandNachziehen()` stellt die
 * Frage jetzt an den Zahlen: es holt zurück, wenn es den Überstand selbst
 * freihält.
 */
function seiteZurueck(): void {
  if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0)
}

/**
 * Ein Fahrtenschreiber für die Höhe.
 *
 * **Weil das Hinsehen den Fehler behebt.** Aus dem Betrieb kam ein
 * Bildschirmfoto mit dem Streifen und eine Minute später die Diagnosekarte mit
 * „874 von 874" -- alles in Ordnung. Die Karte misst `100dvh`, indem sie einen
 * Kasten ins Dokument hängt; das erzwingt einen Neuaufbau und räumt genau den
 * Zustand weg, den sie zeigen soll. Ein Messgerät, das man erst befragen muss,
 * kann diesen Fehler nicht sehen.
 *
 * Also wird mitgeschrieben, und zwar **nur aus freien Werten**: `innerHeight`,
 * die sichtbare Fläche, der Bildschirm und der Kasten der Anwendung. Keiner
 * davon hängt einen Kasten ins Dokument, keiner erzwingt etwas. `100dvh` steht
 * bewusst **nicht** darin -- es wäre wieder der Beobachter, der stört.
 *
 * Fünfundzwanzig Einträge reichen: was zwischen dem Chat und den
 * Einstellungen geschieht, sind eine Handvoll Ereignisse.
 */
export interface Hoehenstand {
  zeit: string
  anlass: string
  fenster: number
  sicht: number
  schirm: number
  shell: number | null
  /**
   * Wo die unterste Leiste endet — Eingabezeile oder Kartenleiste.
   *
   * **Sie trennt die beiden verbliebenen Erklärungen.** Aus dem Betrieb: auf
   * der Einstellungsseite reicht die Anwendung bis zur Unterkante, im Chat
   * bleiben 62 Punkte in der Farbe des Dokuments frei. Ist hier `shell` gleich
   * dem Fenster und diese Zahl kleiner, sitzt der Fehler **in der Seite**; ist
   * `shell` selbst kleiner, sitzt er im Gerüst darüber. Ohne diese Zahl bleibt
   * es beim Raten.
   */
  zeile: number | null
  /**
   * Wie weit die **Seite** verschoben ist, und wie weit die **Fläche**.
   *
   * **Sie trennen die beiden Erklärungen für „nach oben geschoben".** Aus dem
   * Betrieb, an der Personenverwaltung: nach dem Schließen der Tastatur endete
   * die Anwendung bei 655 von 874 Punkten, und oben fehlte die Kopfzeile --
   * derselbe Betrag. Verschoben war also etwas, nur was?
   *
   * `seite` ist `window.scrollY`: das Dokument. `versatz` ist
   * `visualViewport.offsetTop`: die sichtbare Fläche, die iOS über dem
   * Dokument verschiebt. Der erste Anlauf hat die Seite zurückgeholt und nichts
   * geändert -- ohne beide Zahlen bleibt der zweite wieder ein Raten.
   */
  seite: number
  versatz: number
  /**
   * Was **gesetzt** ist und was **freigehalten** wird.
   *
   * **Sie trennen „der Kasten wandert" von „wir schieben ihn".** Aus dem
   * Betrieb, iPhone 17 Pro Max: der Kasten wechselte zwanzig Mal je Sekunde
   * zwischen 894 und 956, und aus den bisherigen Zahlen war nicht zu sehen,
   * ob `--app-hoehe` mitwanderte oder ob `100dvh` allein flackerte. Genau
   * daran hängt aber, ob die Anwendung das Flackern abstellen kann: `html`
   * ist `max(100dvh, --app-hoehe)`, und wer dort die Mindesthöhe stehen hat,
   * gewinnt dieses `max()` immer.
   *
   * Der Abstand daneben, weil er die Unterkante erklärt: `Anwendung −
   * Abstand` ist, wo die Eingabezeile endet. Steht das nicht da, muss man es
   * aus zwei Zeilen zurückrechnen -- und genau das hat eine Runde gekostet.
   */
  gesetzt: number | null
  abstand: number
  pfad: string
}

const VERLAUF: Hoehenstand[] = []
const VERLAUF_LAENGE = 25

function merken(anlass: string): void {
  const shell = document.querySelector('.shell')
  const stand: Hoehenstand = {
    zeit: new Date().toLocaleTimeString('de-DE'),
    // **Der Befund steht am Anlass, nicht als eigener Eintrag.** Die
    // Unterdrückung darunter vergleicht Werte und keinen Anlass -- ein
    // eigener Eintrag „Fläche verworfen" käme direkt nach dem Ereignis mit
    // denselben Zahlen und würde verschluckt. So trägt ihn der Eintrag, der
    // ohnehin geschrieben wird. Der Rohwert bleibt in `sicht` stehen.
    anlass: (sichtVerworfen() ? `${anlass} · Fläche verworfen` : anlass)
      + (gezoomt() ? ` · gezoomt ×${(window.visualViewport?.scale ?? 1).toFixed(1)}` : ''),
    fenster: Math.round(window.innerHeight),
    sicht: Math.round(window.visualViewport?.height ?? 0),
    schirm: Math.round(window.screen.height),
    shell: shell === null ? null : Math.round(shell.getBoundingClientRect().height),
    zeile: (() => {
      const unten = document.querySelector('.composer, .karte__leiste, .scroll-panel')
      return unten === null ? null : Math.round(unten.getBoundingClientRect().bottom)
    })(),
    seite: Math.round(window.scrollY),
    versatz: Math.round(window.visualViewport?.offsetTop ?? 0),
    // Der **eingetragene** Wert, nicht der gerechnete: gefragt ist, was in der
    // Seite steht.
    gesetzt: (() => {
      const roh = parseFloat(document.documentElement.style.getPropertyValue('--app-hoehe'))
      return Number.isFinite(roh) ? Math.round(roh) : null
    })(),
    abstand: (() => {
      const roh = parseFloat(document.documentElement.style.getPropertyValue('--tastatur-hoehe'))
      return Number.isFinite(roh) ? Math.round(roh) : 0
    })(),
    pfad: location.pathname,
  }

  // **Nur, wenn sich etwas geändert hat.** Sonst schiebt ein Scrollen die
  // interessanten Einträge aus dem Fenster, bevor jemand sie liest.
  const letzter = VERLAUF[0]
  if (
    letzter !== undefined
    && letzter.fenster === stand.fenster
    && letzter.sicht === stand.sicht
    && letzter.shell === stand.shell
    && letzter.zeile === stand.zeile
    && letzter.gesetzt === stand.gesetzt
    && letzter.abstand === stand.abstand
    && letzter.seite === stand.seite
    && letzter.versatz === stand.versatz
    && letzter.pfad === stand.pfad
  ) return

  VERLAUF.unshift(stand)
  if (VERLAUF.length > VERLAUF_LAENGE) VERLAUF.length = VERLAUF_LAENGE
}

/** Der Mitschrieb, neueste zuerst. */
export function hoehenVerlauf(): Hoehenstand[] {
  return VERLAUF.slice()
}

/**
 * Und ein Beobachter am Kasten der Anwendung selbst.
 *
 * Er meldet sich **genau dann, wenn der Streifen entsteht oder verschwindet**
 * -- ohne Umfrage und ohne Zeitgeber. Wird erst eingehängt, wenn `.shell`
 * dasteht; beim ersten Lauf gibt es sie noch nicht.
 *
 * # Und er rechnet nach, statt nur zu protokollieren
 *
 * Aus dem Betrieb, mit Video und Mitschrieb vom iPhone 17 Pro Max: bei offener
 * Tastatur lag die Eingabezeile hinter der schwebenden iOS-Leiste. Der
 * Mitschrieb nennt den Betrag:
 *
 * ```
 * 956 / 543 / 956 / 543     Kasten 956, Abstand 413 -- richtig
 * 894 / 543 / 894 / 481     Kasten 894 (aus `100dvh`), Abstand 351
 * 956 / 543 / 956 / 605     Kasten wieder 956, Abstand immer noch 351
 * ```
 *
 * `956 − 351 = 605`, und genau dort steht die Zeile im Video. Die 62 Punkte
 * Unterschied sind der Streifen unter der Statusleiste, den iOS verliert
 * (siehe `vollbildHoehe()`) -- er bewegt `100dvh` und damit den Kasten, ohne
 * dass ein Fensterereignis kommt.
 *
 * **Der Abstand hing an Weckrufen, und dieser war keiner.** Zwischen zwanzig
 * Meldungen „Kasten der Anwendung" in einer Sekunde stand keine einzige
 * Neuberechnung; eingefangen hat es erst die Wache eine Sekunde später. Das
 * ist dasselbe Loch wie zweimal zuvor in dieser Datei: die Formel hätte längst
 * den richtigen Wert ergeben, sie wurde nur nie gefragt.
 *
 * **Und die Zeile steht dabei nicht falsch, sie springt.** Die beiden Stände
 * wechseln sich zwanzig Mal je Sekunde ab und über Sekunden hinweg; die
 * Unterkante wandert mit, 543 / 481 / 543 / 481. Ein einzelner Übergang zeigt
 * das nicht -- der Browserlauf misst deshalb eine ganze Reihe und ihre
 * Spannweite. Steht die Zeile still, sind kleinster und größter Wert
 * derselbe.
 *
 * **Die Höhe wird dabei *nicht* neu gemessen**, nur der Abstand. Ein
 * geänderter Kasten ist keine neue Auskunft über das Fenster --
 * `hoeheNachmessen()` setzt aber `--app-hoehe` und ändert damit den Kasten:
 * das wäre eine Schleife.
 *
 * **Beobachtet wird der Randkasten und nicht der Innenraum.** Der
 * freigehaltene Platz ist ein `padding-bottom` an `.shell`; bei `border-box`
 * verkleinert er den Innenraum, ohne die Höhe zu ändern. Am Innenraum
 * gemessen löste jede Berechnung die nächste aus.
 */
function shellBeobachten(nachrechnen: () => void): void {
  if (typeof ResizeObserver === 'undefined') return
  const shell = document.querySelector('.shell')
  if (shell === null) {
    window.setTimeout(() => shellBeobachten(nachrechnen), 500)
    return
  }
  new ResizeObserver(() => {
    merken('Kasten der Anwendung')
    nachrechnen()
  }).observe(shell, { box: 'border-box' })
}

export function tastaturBeobachten(): void {
  // **Die Höhe zuerst, und ohne `visualViewport`.** Sie ist die wichtigere
  // der beiden Zahlen und darf nicht daran hängen, ob es die Schnittstelle
  // gibt -- sonst stünde auf einem Browser ohne sie wieder `100dvh` da.
  hoeheNachmessen('Start')

  const sicht = window.visualViewport

  /** Der Zeitgeber der Wache -- läuft nur, solange ein Abstand steht. */
  let wache: number | null = null

  /** War das Fenster beim letzten Blick geschrumpft? Für das beobachtete
      Schließen: erst der Übergang klein → voll heißt „Tastatur zu". */
  let fensterWarKlein = false

  /**
   * Den freigehaltenen Platz nachrechnen -- bei **jedem** Weckruf.
   *
   * Aus dem Betrieb, zwei Meldungen mit derselben Signatur: am iPad wurde bei
   * offener Bildschirmtastatur eine HW-Tastatur angeschlossen, am iPhone bei
   * offener Tastatur ein Bildschirmfoto gemacht. Beide Male verschwindet die
   * Tastatur, **ohne dass jemand das Feld loslässt** -- und beide Male blieb
   * ein grauer Bereich stehen, genau wo die Tastatur war.
   *
   * Der Abstand wurde bis dahin nur bei den Ereignissen der sichtbaren Fläche
   * und bei `focusin`/`focusout` nachgerechnet. Meldet sich das Verschwinden
   * der Tastatur nur auf Fensterebene, blieb der alte Wert stehen -- die
   * Formel hätte längst 0 ergeben, sie wurde nur nie gefragt. Deshalb hängt
   * die Rechnung jetzt an denselben Weckrufen wie die Höhe.
   */
  const abstandNachziehen = () => {
    if (!sicht) return
    // Beim Fingerzoom ist die sichtbare Fläche der Ausschnitt und kein
    // Tastaturmaß -- siehe `hoeheNachmessen()`.
    if (gezoomt()) return
    // Durch die Verwerfungsgrenze: mit `height = −33554432` lief die Rechnung
    // unten auf 33,5 Millionen und der Deckel hielt die halbe Anwendung frei
    // -- der breite Zwischenraum aus der Meldung.
    //
    // **Ist die Fläche verworfen, misst das Fenster.** Der erste Rückfall war
    // Abstand 0 -- und dann lag das Feld unter der Tastatur, bis das Tippen
    // es hervorholte, denn iOS' Freistellen ist im verkeilten Zustand selbst
    // kaputt. `innerHeight` blieb dort verlässlich (874 → 472 → 874); der
    // Überstand der Anwendung unter der Fensterkante ist das Verdeckte.
    // Wo das Fenster nicht schrumpft (Browser-Tab, Rechner) oder die
    // Anwendung mitschrumpft (Android), kommt 0 heraus -- wie bisher.
    const masse = sichtMasse()
    const voll = vollbildHoehe()
    const fenster = Math.round(window.innerHeight)
    const fensterKlein = voll !== null && fenster < voll - SCHWELLE

    // **Der Glaube wird nur aus verlässlichen Signalen gespeist.** Ein
    // geschrumpftes Fenster oder eine plausible kleine Fläche heißt offen --
    // und die Höhe wird gemerkt, für die dritte Spielart, in der beides
    // schweigt. Wächst eine verlässliche Quelle zurück, heißt das zu. Die dritte
    // Spielart selbst ändert den Glauben nie.
    if (fensterKlein && voll !== null) {
      tastaturMerken(voll - fenster)
      tastaturVermutet = true
    } else if (fensterWarKlein && !fensterKlein) {
      // Beobachtetes Schließen auf Fensterebene -- der Fall vom Mitschrieb
      // um 00:23:57, in dem die Zeile danach richtig unten stand.
      tastaturVermutet = false
    }
    fensterWarKlein = fensterKlein
    if (masse !== null && voll !== null) {
      if (masse.hoehe < voll - SCHWELLE) {
        tastaturMerken(voll - masse.hoehe)
        tastaturVermutet = true
      } else {
        tastaturVermutet = false
      }
    }

    // **Wie weit die Anwendung unter die sichtbare Fläche ragt** -- gemessen,
    // nicht aus dem Fenster gerechnet.
    //
    // Hier stand `window.innerHeight - sicht.height`, mit der Begründung, die
    // Differenz sei genau die Tastatur. Auf iOS ist sie das nicht: das Fenster
    // ist dort **nicht** die Höhe der Anwendung. Aus dem Betrieb, Mitschrieb
    // auf der Personenseite bei offener Tastatur:
    //
    //     730 / 478 / 812 / 438 · 374↕334
    //
    // Fenster 730, Anwendung 812. Der alte Ausdruck ergibt 252, gebraucht wird
    // aber der Überstand der **Anwendung** über die sichtbare Fläche -- und
    // iOS hat die Seite obendrein schon um 374 verschoben, um das Feld
    // freizustellen. Wer beides addiert, schiebt den Inhalt zweimal hoch:
    // genau der breite Streifen aus der Meldung.
    //
    // Beides steckt in einer einzigen Messung: die Unterkante der Anwendung im
    // Bild gegen die Unterkante des Sichtbaren. Hat iOS schon genug
    // verschoben, kommt 0 heraus; überlagert die Tastatur die Anwendung, kommt
    // genau die Überlagerung heraus. Der frühere Ausdruck ist der Sonderfall
    // davon, in dem nichts verschoben ist.
    const kasten = document.documentElement.getBoundingClientRect()
    const unten = kasten.bottom
    // **Wo gemessen wird, zählt die Höhe der Anwendung, nicht ihre Unterkante**
    // (#34).
    //
    // Der Absatz darüber beschreibt, was bis dahin an *allen* drei Zweigen
    // stand, und für die beiden hinteren gilt er weiter. Er verrechnet iOS'
    // Verschieben absichtlich mit: hat
    // iOS die Seite schon hochgeschoben, bleibt 0 freizuhalten, weil das
    // Verschieben die Arbeit bereits getan hat. Das stimmt für die
    // Eingabezeile -- und **es zahlt sie mit der Kopfzeile**: die steht dann
    // oben außerhalb des Bildes. Aus dem Betrieb, mit Video: bei offener
    // Tastatur fehlte die Überschrift durchgehend, und weil die Seite selbst
    // verschiebbar blieb, ließ sich die Eingabezeile vollends unter die
    // Tastatur schieben.
    //
    // Der Überstand ist deshalb der *ungeschobene*: wie weit die Anwendung
    // über die sichtbare Fläche hinausreicht, wenn die Seite oben steht. Die
    // Höhe des Kastens ist genau das und hängt an keinem Scrollstand.
    // Zurückgeholt wird die Seite gleich darunter -- beides gehört zusammen,
    // sonst stünde die Zeile kurz unter der Tastatur.
    //
    // Drei Zweige, vom Verlässlichsten zum Letzten: die gemessene Fläche; das
    // Fenster, wenn sie verworfen ist; und die Erinnerung, wenn beide
    // schweigen -- die angenommene Sichtkante liegt dann bei
    // Bildschirmhöhe minus gemerkter Tastatur. **Die beiden hinteren rechnen
    // weiter mit der Unterkante**: dort wird die Seite auch nicht
    // zurückgeholt, und ohne verlässliche Fläche ist iOS' Verschieben das
    // einzige, worauf man sich stützen kann.
    const roh = masse !== null
      ? kasten.height - masse.hoehe
      : fensterKlein || voll === null || gemerkteTastatur === null || !tastaturVermutet
        ? unten - fenster
        : unten - (voll - gemerkteTastatur)

    // Nach oben begrenzt, damit ein Ausreißer in der Messung die Anwendung
    // nicht zusammenfalten kann -- die Regel steht in `abstandAus()`, samt
    // dem Grund, warum der Deckel nicht mehr die halbe Anwendung ist.
    const abstand = abstandAus(kasten.height, roh, schreibtJemand())

    document.documentElement.style.setProperty('--tastatur-hoehe', `${abstand}px`)
    letzterAbstand = abstand

    /*
     * **Und dann steht die Seite wieder oben** (#34).
     *
     * Aus dem Betrieb, mit Video: „aktuell ist der Bereich mit Überschrift und
     * Nachrichtenzeile scrollbar, wenn Bildschirmtastatur eingeblendet ist.
     * Das führt dazu, dass die Nachrichtenzeile unter die Tastatur
     * verschwinden kann." Im Video ist beides zu sehen -- bei offener Tastatur
     * fehlt die Überschrift, und bei 0:32 steht über der iOS-Leiste nur noch
     * der Verlauf.
     *
     * **Zwei Wege halten die Zeile über der Tastatur, und bisher lief der
     * falsche.** Entweder hält die Anwendung den Platz selbst frei
     * (`--tastatur-hoehe`), oder iOS schiebt die ganze Seite hoch. Der zweite
     * kostet die Kopfzeile und lässt die Seite verschiebbar; der erste kostet
     * nichts. Also: Platz freihalten -- und die Verschiebung zurücknehmen.
     *
     * **Nur mit verlässlich gemessener Fläche, und nur wenn der freigehaltene
     * Platz den Überstand wirklich deckt.** Das ist die Lehre aus `f60ad9a`,
     * das genau hier zurückgeholt hat und den Chat gebrochen hat („keine
     * Eingabezeile nach Aufruf der Tastatur"): im verkeilten Zustand ist die
     * Fläche Müll, der Abstand deshalb 0 -- und wer dann die Seite zurückholt,
     * schiebt das Feld unter die Tastatur, ohne etwas an seine Stelle zu
     * setzen. Deckt der Abstand den Überstand dagegen, liegt die Unterkante
     * der Anwendung bei ungeschobener Seite genau an der Sichtkante, und die
     * Zeile steht, wo sie hingehört.
     *
     * Ist gar kein Überstand da (Rechner, Android, iPad -- das Fenster
     * schrumpft mit), ist die Bedingung ohnehin erfüllt und die Seite bleibt
     * oben, wie sie es dort immer war.
     *
     * **Ohne Fokus dagegen immer, auch ohne verlässliche Fläche.** Die Lehre
     * aus `f60ad9a` betrifft den Fall, dass jemand schreibt: dort steht ein
     * Feld, das freigestellt bleiben muss. Ohne Feld gibt es nichts
     * freizustellen -- und eine verschoben stehengebliebene Seite ist genau
     * der Fehler vom iPhone 16 Pro (`Abstand 0`, `Seite 431`, und zu sehen
     * war der leere Streifen unter der Eingabezeile).
     */
    if (!schreibtJemand()) seiteZurueck()
    else if (masse !== null && darfZurueck(roh, abstand, true)) seiteZurueck()

    // **Die Safe-Area weicht der Tastatur.** Aus dem Betrieb, nachgemessen
    // am Bildschirmfoto: das Feld stand an der Fensterkante -- plus die
    // ~34 Punkte `env(safe-area-inset-bottom)`, die iOS bei offener Tastatur
    // weiter meldet, obwohl der Balkenbereich längst hinter ihr liegt.
    // WhatsApp klappt diesen Abstand ein; hier tut es die Variable, auf die
    // die unteren Safe-Area-Angaben in `app.css` zurückfallen.
    // **Dieselbe Zahl wie der Abstand** (0.47.1): `bildschirmtastaturVerdeckt()`
    // liest `letzterAbstand`, gesetzt zwei Absätze weiter oben. Ein zweiter
    // Test hat hier die Zeile eine Sekunde lang 34 Punkte zu hoch gestellt.
    if (bildschirmtastaturVerdeckt()) {
      document.documentElement.style.setProperty('--sicherheit-unten', '0px')
    } else {
      document.documentElement.style.removeProperty('--sicherheit-unten')
    }

    /*
     * **Und ein Merkmal für alle, die bei offener Tastatur beiseitetreten.**
     *
     * Aus dem Betrieb: „das + links vom Eingabefeld funktioniert NICHT, wenn
     * die Tastatur geöffnet ist." Der Grund ist derselbe wie beim Anmeldeknopf
     * damals: ein Tipp darauf nimmt dem Feld den Fokus, iOS fährt die Tastatur
     * herunter, die Eingabezeile wandert dabei um deren Höhe nach unten -- und
     * das Loslassen trifft den Knopf nicht mehr. Es kommt gar kein `click` an.
     *
     * **Den Fokus zu halten wäre der andere Weg und ist verworfen** (so machen
     * es die Auszeichnungsknöpfe daneben, mit `preventDefault` am
     * `mousedown`): „ansonsten haben wir nur wieder weitere Probleme, wenn
     * sich statt der Tastatur der Foto-Ordner / GIF-Auswahl öffnen sollen."
     * Eine gehaltene Tastatur unter einem aufklappenden Blatt und einem
     * Dateidialog ist die nächste Runde Sonderfälle.
     *
     * **`schreibtJemand()` steht mit gutem Grund daneben.** Bliebe das Merkmal
     * einmal hängen, wäre das hier kein grauer Balken, sondern ein
     * verschwundener Knopf -- der schlechtere Fehler. Ohne Fokus im Feld gibt
     * es keine Bildschirmtastatur, und `focusout` rechnet sofort nach; damit
     * heilt es sich selbst, auch wenn die Messung einmal klemmt.
     *
     * Ein Merkmal am Dokument und keine Zustandsverwaltung: dieselbe Bauweise
     * wie `data-schrift` in `lib/darstellung.ts`, und die Regel dazu steht in
     * `app.css` neben dem Knopf, den sie betrifft.
     */
    if (bildschirmtastaturVerdeckt() && schreibtJemand()) {
      document.documentElement.setAttribute('data-tastatur', 'an')
    } else {
      document.documentElement.removeAttribute('data-tastatur')
    }

    /**
     * Die Wache: solange unten etwas freigehalten wird, wird nachgeprüft.
     *
     * Aus dem Betrieb (#31), zum wiederholten Mal und diesmal ohne Ausweg
     * über die Ereignisse: Tastatur eingeblendet, Bildschirmfoto erstellt --
     * die Tastatur verschwindet, der graue Balken bleibt. Der Fokus bleibt
     * dabei im Feld, `focusout` kommt nie, und iOS feuert in diesem Moment
     * **gar kein Ereignis**: kein `resize`, nichts an der sichtbaren Fläche.
     * Alle Nachmessungen hängen aber an Weckrufen -- die Formel hätte längst
     * 0 ergeben, es fragte sie nur wieder niemand. Dasselbe Loch wie damals
     * bei den Fensterereignissen, eine Ebene tiefer.
     *
     * Deshalb: ein freigehaltener Abstand ist ein Ausnahmezustand, und nur
     * in ihm läuft ein Zeitgeber -- einmal je Sekunde nachmessen, bis der
     * Abstand 0 ist, dann endet die Wache von selbst. Im Normalbetrieb (kein
     * Abstand) läuft nichts. Der Mitschrieb bleibt sauber, weil `merken`
     * unveränderte Stände ohnehin unterdrückt.
     */
    if (abstand > 0 && wache === null) {
      wache = window.setInterval(() => {
        hoeheNachmessen('Wache')
        abstandNachziehen()
      }, 1000)
    } else if (abstand === 0 && wache !== null) {
      window.clearInterval(wache)
      wache = null
    }
  }

  // **Erst hier, weil er jetzt nachrechnet.** Der Beobachter stand bisher
  // gleich hinter der ersten Messung; er brauchte nichts als `merken`. Der
  // Abstand ist eine Ebene tiefer und steht deshalb weiter unten -- ohne
  // `visualViewport` tut `abstandNachziehen()` von sich aus nichts, der
  // Mitschrieb läuft trotzdem weiter.
  shellBeobachten(() => abstandNachziehen())

  const messen = (anlass: string) => () => {
    hoeheNachmessen(anlass)
    abstandNachziehen()
  }

  window.addEventListener('resize', messen('Fenster verändert'))
  // **Und die Seite bleibt oben, nicht nur wenn gerade gemessen wird** (#34).
  //
  // Zurückgeholt wurde bisher nur bei den Weckrufen der Höhe. Wischt jemand
  // bei offener Tastatur über die Seite, kommt keiner davon -- und die
  // Verschiebung bliebe bis zum nächsten Ereignis stehen. Genau daran ließ
  // sich die Eingabezeile unter die Tastatur schieben.
  //
  // `abstandNachziehen` entscheidet dabei wie überall selbst, ob zurückgeholt
  // werden darf; der Überstand hängt an keinem Scrollstand, die Rechnung ist
  // hier also dieselbe wie sonst. Eine Schleife entsteht nicht: `seiteZurueck`
  // rührt eine Seite, die schon oben steht, nicht an.
  window.addEventListener('scroll', () => abstandNachziehen(), { passive: true })
  // Beim Drehen meldet Safari die neue Höhe erst nach dem Ereignis; ein
  // `resize` kommt zwar hinterher, aber nicht überall verlässlich.
  window.addEventListener('orientationchange', messen('gedreht'))
  // **Zurück aus dem Hintergrund heißt: neu messen.** Was zwischendurch am
  // Fenster geschehen ist, hat diese Seite nicht mitbekommen.
  window.addEventListener('pageshow', messen('Seite gezeigt'))
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) messen('zurück im Vordergrund')()
  })

  if (!sicht) {
    // Ohne die Schnittstelle bleibt es beim bloßen Höhensetzen: kein Abstand.
    return
  }

  /**
   * **Nachmessen, weil iOS am Ende der Animation nichts meldet** (0.47.1).
   *
   * Die Ereignisse kommen zu Beginn des Öffnens, mit Zwischenständen --
   * Fenster 416 bei Fläche 416 --, und am Ende kommt keines mehr. Was der
   * erste Durchlauf aus einem Zwischenstand setzt, korrigierte bisher erst
   * die Wache im Sekundentakt. Zwei kurze Nachmessungen nach jedem Anlass
   * (150 und 400 ms; die Tastatur braucht rund 250) holen das Ende der
   * Animation ein. Ein neuer Anlass verwirft die alten, damit nie zwei
   * Reihen laufen. Der Mitschrieb bleibt sauber: unveränderte Stände
   * unterdrückt `merken` ohnehin.
   *
   * **Und nur, solange jemand schreibt.** Ohne Fokus in einem Feld gibt es
   * keine Tastatur, deren Animation abzuwarten wäre -- der Aufruf beim Start
   * und die Weckrufe ohne Fokus lösen also nichts aus. Gemessen am
   * Prüfstand: `tastaturfeld.mjs` setzt `--tastatur-hoehe` von Hand, und
   * eine Nachmessung aus dem Start hat sie 150 ms später wieder auf 0
   * gestellt. Dieselbe zweite Sperre wie bei `verdecktAus()`.
   */
  let nachmessungen: number[] = []
  const nachmessenPlanen = () => {
    for (const zeitgeber of nachmessungen) window.clearTimeout(zeitgeber)
    if (!schreibtJemand()) {
      nachmessungen = []
      return
    }
    nachmessungen = [150, 400].map((nach) =>
      window.setTimeout(() => {
        hoeheNachmessen('Nachmessung')
        abstandNachziehen()
      }, nach),
    )
  }
  const anpassen = () => {
    messen('sichtbare Fläche')()
    nachmessenPlanen()
  }
  anpassen()
  sicht.addEventListener('resize', anpassen)
  sicht.addEventListener('scroll', anpassen)
  // **Beim Verlassen sofort messen.** Die sichtbare Fläche wächst danach zwar
  // ohnehin und löst ein `resize` aus -- aber erst, wenn die Tastatur
  // heruntergefahren ist. Bis dahin stünde der Abstand noch da, obwohl längst
  // niemand mehr schreibt.
  //
  // `focusin` ebenso: wer aus einem Feld in ein anderes wechselt, hat
  // durchgehend eine Tastatur, und ohne dieses Ereignis fiele der Abstand
  // dazwischen kurz weg.
  // Der Fokus speist den Glauben: ein Feld antippen öffnet auf dem Telefon
  // die Tastatur, das Loslassen schließt sie. In den messbaren Spielarten
  // wird das gleich darauf ohnehin gemessen; in der dritten ist es das
  // einzige Signal, das es noch gibt.
  window.addEventListener('focusin', () => {
    tastaturVermutet = true
    anpassen()
  })
  window.addEventListener('focusout', () => {
    tastaturVermutet = false
    anpassen()
  })
}
